import React, { useEffect, useState, useCallback, useRef } from "react"
import Button from "aldoo-ra/Button"
import { XMarkIcon, ClipboardIcon } from "@heroicons/react/24/outline"
import MediaLibrary from "aldoo-ra/MediaLibrary"
import ReactDOM from "react-dom"

// This lightbox portals to document.body and is often opened from inside the
// MediaLibrary modal, which sits on the ModalStack (Z_BASE ~1.05e9). It must
// render above that, so it sits near the top of the z range. The nested
// "Replace Image" MediaLibrary is one step higher so it stacks above the preview.
const PREVIEW_Z = 2147483000
const PREVIEW_MEDIA_LIBRARY_Z = PREVIEW_Z + 1

const ImagePreview = ({
  isOpen,
  imageUrl,
  fileName,
  onClose,
  onImageSelect,
  selectedImageIndex = null,
  // Light mode options
  mode = "full", // "full" or "light"
  title = null, // Custom title for light mode
  enableZoomOnScroll = false, // Enable zoom with mouse wheel
  zoomMin = 0.25, // Minimum zoom level
  zoomMax = 1.5, // Maximum zoom level
  zoomStep = 0.05, // Zoom increment step
  defaultZoom = 1, // Default zoom level
}) => {
  const [scale, setScale] = useState(defaultZoom)
  const [isDragging, setIsDragging] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [copySuccess, setCopySuccess] = useState({
    filename: false,
    url: false,
  })
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)
  const containerRef = useRef(null)
  // Add state for image dimensions and file size
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  })
  const [imageSize, setImageSize] = useState(null)

  const resetView = useCallback(() => {
    setScale(1)
    setPosition({ x: 0, y: 0 })
  }, [])

  // Function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0B"
    const k = 1024
    const sizes = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i]
  }

  // Function to calculate image size from URL
  const calculateImageSize = useCallback(async (url) => {
    try {
      const response = await fetch(url, { method: "HEAD" })
      if (response.ok) {
        const contentLength = response.headers.get("content-length")
        if (contentLength) {
          const bytes = parseInt(contentLength, 10)
          const formattedSize = formatFileSize(bytes)
          setImageSize(formattedSize)
        } else {
          setImageSize("Unknown")
        }
      }
    } catch (error) {
      console.error("Failed to fetch image size:", error)
      setImageSize("Unknown")
    }
  }, [])

  const handleCopyFileName = async () => {
    try {
      await navigator.clipboard.writeText(fileName)
      setCopySuccess((prev) => ({ ...prev, filename: true }))
      setTimeout(
        () => setCopySuccess((prev) => ({ ...prev, filename: false })),
        2000
      )
    } catch (err) {
      console.error("Failed to copy filename:", err)
    }
  }

  const handleCopyImageUrl = async () => {
    try {
      await navigator.clipboard.writeText(imageUrl)
      setCopySuccess((prev) => ({ ...prev, url: true }))
      setTimeout(
        () => setCopySuccess((prev) => ({ ...prev, url: false })),
        2000
      )
    } catch (err) {
      console.error("Failed to copy image URL:", err)
    }
  }

  useEffect(() => {
    resetView()
  }, [imageUrl, isOpen, resetView])

  // Calculate image size when URL changes
  useEffect(() => {
    if (imageUrl && isOpen) {
      calculateImageSize(imageUrl)
    }
  }, [imageUrl, isOpen, calculateImageSize])

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose()
      }
    }
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("keydown", handleEscape)
    }
  }, [isOpen, onClose])

  const handleWheel = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()

    const delta = -Math.sign(e.deltaY)
    const zoomFactor = 0.1

    const container = containerRef.current
    if (!container) return

    // Get mouse position relative to container
    const rect = container.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top

    setScale((prevScale) => {
      const newScale = Math.min(
        Math.max(prevScale + delta * zoomFactor, zoomMin),
        zoomMax
      )

      // Calculate new position to zoom towards mouse cursor
      const scaleRatio = newScale / prevScale
      setPosition((prevPosition) => ({
        x: mouseX - (mouseX - prevPosition.x) * scaleRatio,
        y: mouseY - (mouseY - prevPosition.y) * scaleRatio,
      }))

      return newScale
    })
  }, [zoomMin, zoomMax])

  useEffect(() => {
    const element = containerRef.current
    if (element && isOpen && enableZoomOnScroll) {
      const wheelHandler = (e) => handleWheel(e)
      element.addEventListener("wheel", wheelHandler, { passive: false })
      return () => element.removeEventListener("wheel", wheelHandler)
    }
  }, [handleWheel, isOpen, enableZoomOnScroll])

  const handleMouseDown = useCallback(
    (e) => {
      if (e.button === 0) {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(true)
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        })
      }
    },
    [position]
  )

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging) {
        e.preventDefault()
        const newX = e.clientX - dragStart.x
        const newY = e.clientY - dragStart.y
        setPosition({
          x: newX,
          y: newY,
        })
      }
    },
    [isDragging, dragStart]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleSliderChange = (e) => {
    const value = parseFloat(e.target.value)
    setScale(value)
    // Keep position at (0, 0) to maintain centering
    setPosition({ x: 0, y: 0 })
  }

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleMouseUp)
      return () => {
        window.removeEventListener("mousemove", handleMouseMove)
        window.removeEventListener("mouseup", handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  useEffect(() => {
    if (isOpen && containerRef.current) {
      containerRef.current.focus()
    }
  }, [isOpen])

  const handleMediaLibraryOpen = (e) => {
    e.stopPropagation()
    setIsMediaLibraryOpen(true)
  }

  const handleFileSelect = (file) => {
    // Handle the selected file from Media Library
    if (onImageSelect) {
      // Here's the key fix - we're passing the file with our stored selectedImageIndex
      // and a flag to indicate it's coming from the preview
      const fileWithIndex = {
        ...file,
        selectedIndex: selectedImageIndex,
        fromPreview: true,
      }
      onImageSelect(fileWithIndex)

      // Just close the media library, not the preview
      setIsMediaLibraryOpen(false)
    }
  }

  if (!isOpen) return null

  const isLightMode = mode === "light"

  // Light mode - simple preview with zoom
  if (isLightMode) {
    const lightPreviewContent = (
      <div
        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center"
        style={{ zIndex: PREVIEW_Z }}
        onClick={onClose}
      >
        <div
          className="bg-admin_canvas w-screen h-screen flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-admin_paper border-b border-admin_bordered px-6 py-4 flex items-center justify-between">
            <h2 className="text-admin_text text-lg font-semibold">
              {title || fileName || "Image Preview"}
            </h2>
            <div className="flex items-center gap-4">
              <Button
                variant="admin_secondary"
                className="text-admin_text hover:bg-admin_neutral_3"
                onClick={(e) => {
                  e.stopPropagation()
                  resetView()
                }}
              >
                Reset View
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-admin_text text-sm">{zoomMin}x</span>
                <input
                  type="range"
                  min={zoomMin}
                  max={zoomMax}
                  step={zoomStep}
                  value={scale}
                  onChange={handleSliderChange}
                  className="w-48 h-2 bg-admin_neutral_3 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-admin_text text-sm">{zoomMax}x</span>
                <span className="text-admin_text text-sm ml-2 w-12">
                  {scale.toFixed(2)}x
                </span>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-admin_neutral_3 transition-colors duration-200"
                aria-label="Close preview"
              >
                <XMarkIcon className="w-6 h-6 text-admin_text" />
              </button>
            </div>
          </div>

          {/* Image container with scroll and zoom */}
          <div
            ref={containerRef}
            className="overflow-auto flex-1 p-6 bg-admin_canvas outline-none"
            style={{
              cursor: isDragging ? "grabbing" : "grab",
            }}
            onMouseDown={handleMouseDown}
            tabIndex={0}
          >
            <img
              src={imageUrl}
              alt={title || fileName || "Preview"}
              className="w-full h-auto select-none"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transition: isDragging ? "none" : "transform 0.1s ease-out",
                maxWidth: "100%",
              }}
              draggable="false"
            />
          </div>
        </div>
      </div>
    )

    return ReactDOM.createPortal(lightPreviewContent, document.body)
  }

  // Full mode - advanced preview with zoom, pan, etc.
  const previewContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex flex-col m-0"
      style={{
        zIndex: PREVIEW_Z,
        margin: 0,
        padding: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {/* Top control panel */}
      <div
        className="w-full bg-black bg-opacity-70 p-4 flex items-center gap-4"
        onClick={(e) => e.stopPropagation()}
        style={{ margin: 0 }}
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyFileName}
              className="p-2 rounded-full hover:bg-admin_neutral_3 transition-colors duration-200 text-white"
              aria-label="Copy filename"
            >
              <ClipboardIcon className="w-5 h-5" />
            </button>
            <span className="text-white text-sm">
              {copySuccess.filename ? "Copied!" : fileName}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyImageUrl}
              className="p-2 rounded-full hover:bg-admin_neutral_3 transition-colors duration-200 text-white"
              aria-label="Copy image URL"
            >
              <ClipboardIcon className="w-5 h-5" />
            </button>
            <span className="text-white text-sm">
              {copySuccess.url ? "Copied!" : "Image URL"}
            </span>
          </div>
          {/* Add image resolution and file size information as chips */}
          {imageDimensions.width > 0 && (
            <div className="text-white text-sm bg-gray-800 px-3 py-1 rounded-full border border-admin_bordered">
              Resolution: {imageDimensions.width}x{imageDimensions.height}
            </div>
          )}
          {imageSize && (
            <div className="text-white text-sm bg-gray-800 px-3 py-1 rounded-full border border-admin_bordered">
              Size: {imageSize}
            </div>
          )}
        </div>

        <div className="flex-1 flex justify-end items-center gap-4">
          <Button
            variant="admin_secondary"
            className="w-[150px] text-white hover:bg-admin_neutral_3"
            onClick={handleMediaLibraryOpen}
          >
            Media Library
          </Button>
          <Button
            variant="admin_secondary"
            className="text-white hover:bg-admin_neutral_3 w-32"
            onClick={(e) => {
              e.stopPropagation()
              resetView()
            }}
          >
            Reset View
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-white text-sm">{zoomMin}x</span>
            <input
              type="range"
              min={zoomMin}
              max={zoomMax}
              step={zoomStep}
              value={scale}
              onChange={handleSliderChange}
              className="w-48 h-2 bg-admin_neutral_3 rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-white text-sm">{zoomMax}x</span>
            <span className="text-white text-sm ml-2 w-12">
              {scale.toFixed(2)}x
            </span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
            className="p-2 rounded-full hover:bg-admin_neutral_3 transition-colors duration-200"
            aria-label="Close preview"
          >
            <XMarkIcon className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Image container */}
      <div
        ref={containerRef}
        className="flex-1 w-full overflow-hidden flex items-center justify-center outline-none"
        style={{
          cursor: isDragging ? "grabbing" : "grab",
          margin: 0,
        }}
        onMouseDown={handleMouseDown}
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imageUrl}
          alt="Preview"
          className="select-none"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transition: isDragging ? "none" : "transform 0.1s ease-out",
            objectFit: "contain",
            maxWidth: "100%",
            maxHeight: "100%",
            margin: 0,
          }}
          draggable="false"
          onLoad={(e) => {
            setImageDimensions({
              width: e.target.naturalWidth,
              height: e.target.naturalHeight,
            })
          }}
        />
      </div>

      {/* Media Library Modal */}
      <MediaLibrary
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onFileSelect={handleFileSelect}
        title="Replace Image"
        defaultFileType="image"
        zIndex={PREVIEW_MEDIA_LIBRARY_Z}
      />
    </div>
  )

  // Use createPortal to render directly to document.body
  return ReactDOM.createPortal(previewContent, document.body)
}

export default ImagePreview
